import { ArrowSquareOut, FilePdf, Globe, ImageSquare } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { readLocalViewFile } from '../lib/localContentStore';
import type { PlacedObject } from '../types';
import { Modal } from './Modal';

interface ContentViewerModalProps {
  object: PlacedObject | null;
  onClose: () => void;
}

export function ContentViewerModal({ object, onClose }: ContentViewerModalProps) {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const content = object?.viewContent;

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setResolvedUrl(null);
    if (!content) return;
    if (content.storage === 'local' && content.localKey) {
      setLoading(true);
      readLocalViewFile(content.localKey).then((file) => {
        if (!active || !file) return;
        objectUrl = URL.createObjectURL(file);
        setResolvedUrl(objectUrl);
      }).finally(() => { if (active) setLoading(false); });
    } else {
      setResolvedUrl(content.url ?? null);
    }
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [content]);

  const Icon = useMemo(() => {
    if (content?.type === 'pdf') return FilePdf;
    if (content?.type === 'website') return Globe;
    return ImageSquare;
  }, [content?.type]);

  return (
    <Modal
      open={!!object}
      title={object ? `${object.name} · Read` : '콘텐츠 보기'}
      description={content?.name}
      onClose={onClose}
      className="content-viewer-modal"
    >
      <div className="content-viewer-toolbar">
        <span><Icon size={17} weight="duotone" /> {content?.type === 'pdf' ? 'PDF' : content?.type === 'website' ? '웹사이트' : '이미지'}</span>
        {resolvedUrl && <a href={resolvedUrl} target="_blank" rel="noreferrer">새 창에서 열기 <ArrowSquareOut size={15} /></a>}
      </div>
      <div className="content-viewer-stage">
        {loading && <div className="viewer-loading"><span /> 로컬 콘텐츠를 불러오는 중</div>}
        {!loading && !resolvedUrl && <div className="viewer-empty">등록된 콘텐츠를 불러올 수 없습니다.</div>}
        {!loading && resolvedUrl && content?.type === 'image' && <img src={resolvedUrl} alt={content.name} />}
        {!loading && resolvedUrl && (content?.type === 'pdf' || content?.type === 'website') && (
          <iframe src={resolvedUrl} title={content.name} allow="fullscreen" />
        )}
      </div>
    </Modal>
  );
}
